// --- Core ---
import {map, pick} from "underscore";
// --- SaniQ ---
import {DeserializedRecord, deserializeItem, deserializeManyRelationship, Deserializer, JSONAPIManyRelationship, JSONAPIRecord} from "./Helpers";
import {deserializeVersion, Version, VersionDeserializer} from "./Version";
import {isEmpty} from "helpers/StringUtil";
import {deviceFilterRegex} from "definitions/regex";
import {MeasurementKey} from "vendor/qurasoft/definitions/types/measurements";

interface Device {
  manufacturer?: '-' | '*' | string;
  model?: string;
  valueTypes: Array<MeasurementKey>;
}

interface Link {
  icon: string;
  link_android: string;
  link_ios: string;
  title: string;
}

interface MetaInformation {
  devices?: Array<Device>;
  links?: Array<Link>;
}

interface Cohort extends DeserializedRecord<"tag"> {
  deleteDisabled: boolean;
  key?: string;
  name: string;
  description?: string;
  metaInformation?: MetaInformation;
  versions: Array<Version<Cohort>>;
}

const formatDeviceFilter = ({manufacturer, model, valueTypes}) => {
  if (manufacturer === "-") {
    return "-";
  }
  let result = manufacturer;

  if (manufacturer !== "*") {
    result += `/${model}`;
  }

  if (valueTypes?.length) {
    result += `;${valueTypes.join(',')}`;
  }

  return result;
};

const formatMetaInformation = ({devices, ...other}: MetaInformation = {}) => ({
  ...other,
  devices: devices?.length ? map(devices, formatDeviceFilter) : null
});

const formatCohort = (values: Cohort) => ({
  metaInformation: formatMetaInformation(values.metaInformation),
  ...pick(values, 'description', 'name', 'versionComment')
});

const parseDeviceFilter = (device) => {
  const {groups} = deviceFilterRegex.exec(device);

  return {
    uuid: crypto.randomUUID(),
    ...groups,
    valueTypes: groups.valueTypes?.split(',') || []
  };
};

const deserializeMetaInformation = (metaInformation) => {
  if (!metaInformation) {
    return {};
  }
  const {devices, ...other} = metaInformation;
  const parsedDevices = devices?.length ? map(devices, parseDeviceFilter) : undefined;

  return {
    ...other,
    devices: parsedDevices
  };
};

interface CohortData extends JSONAPIRecord {
  relationships: {
    versions: JSONAPIManyRelationship
  }
}

const deserializeCohort: Deserializer<Cohort, CohortData> = (data, included) => ({
  ...deserializeItem(data),
  deleteDisabled: !isEmpty(data.attributes.key),
  metaInformation: deserializeMetaInformation(data.attributes.meta_information),
  versions: deserializeManyRelationship(data.relationships.versions, included, deserializeVersion as VersionDeserializer<Cohort>)
});

export {
  Cohort,
  deserializeCohort,
  Device as MetaInformationDevice,
  formatCohort,
  Link as MetaInformationLink,
  MetaInformation
};
